import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import NavGroups from "@/components/NavGroups";
import {
  Gauge,
  CarSimple,
  Users,
  Receipt,
  SignOut,
  Lightning,
  Package as PackageIcon,
  Buildings,
  StackSimple,
  ChartBar,
  Bank,
  Wallet,
  UserCircle,
  ArrowsDownUp,
  Gear,
  UsersThree,
  ShieldCheck,
  Key,
  ClipboardText,
  CalendarBlank,
  IdentificationCard,
  Ticket,
  Storefront,
  Megaphone,
  Wrench,
  Broom,
  Hash,
  WarningCircle,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { isQuoteOnlyUser } from "@/lib/dseAccess";
import { isGateOnlyUser } from "@/lib/gatePass";

const NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, end: true, id: "nav-dashboard" },
  { to: "/inventory", label: "Inventory", icon: CarSimple, id: "nav-inventory", perm: "inventory.view", group: "Sales" },
  { to: "/sales", label: "Sales", icon: Receipt, id: "nav-sales", perm: "sales.view", group: "Sales" },
  { to: "/quotes", label: "Quotes", icon: Receipt, id: "nav-quotes", perm: "quotes.view", group: "Sales" },
  { to: "/bookings", label: "Bookings", icon: Ticket, id: "nav-bookings", perm: "bookings.view", group: "Sales" },
  { to: "/registrations", label: "Registrations", icon: ClipboardText, id: "nav-registrations", perm: "registrations.view", group: "Sales" },
  { to: "/post-sale-tasks", label: "Post-Sale Tasks", icon: ClipboardText, id: "nav-post-sale", perm: "post_sale.view", group: "Sales" },
  { to: "/customers", label: "Customers", icon: Users, id: "nav-customers", perm: "customers.view", group: "Sales" },
  { to: "/sub-dealers", label: "Sub-Dealers", icon: Storefront, id: "nav-sub-dealers", perm: "sub_dealers.view", group: "Sales" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, id: "nav-campaigns", perms: ["campaigns.view", "campaigns.create"], group: "Sales" },
  { to: "/pricing", label: "Pricing Engine", icon: PackageIcon, id: "nav-pricing", perm: "pricing.manage", group: "Sales" },
  { to: "/packages", label: "Packages", icon: PackageIcon, id: "nav-packages", perm: "packages.view", group: "Sales" },
  { to: "/financiers", label: "Financiers", icon: Bank, id: "nav-financiers", perm: "financiers.manage", group: "Sales" },
  { to: "/evaluators", label: "Evaluators", icon: UserCircle, id: "nav-evaluators", perm: "evaluators.manage", group: "Sales" },
  { to: "/sales-agents", label: "Sales Agents", icon: IdentificationCard, id: "nav-sales-agents", adminOnly: true, group: "Sales" },
  { to: "/service", label: "Service", icon: Wrench, id: "nav-service", perm: "service.view", group: "Service" },
  { to: "/spare-parts", label: "Required Order", icon: PackageIcon, id: "nav-spare-parts", perm: "service.view", end: true, group: "Service" },
  { to: "/spare-parts/stock", label: "Stock Planning", icon: PackageIcon, id: "nav-stock-planning", perm: "service.view", group: "Service" },
  { to: "/service-staff", label: "Service Staff", icon: IdentificationCard, id: "nav-service-staff", adminOnly: true, group: "Service" },
  { to: "/reports/service-parts-orders", label: "Service Parts Orders", icon: PackageIcon, id: "nav-service-parts-orders", perms: ["service.view", "reports.view"], group: "Service" },
  { to: "/gate", label: "Gate Passes", icon: Ticket, id: "nav-gate-passes", perms: ["gate.view", "gate.scan", "service.view"], group: "Service" },
  { to: "/reports/gate-register", label: "Gate Register", icon: ChartBar, id: "nav-gate-register", perms: ["service.reports.view", "service.reports", "reports.view"], group: "Service" },
  { to: "/reports/service-invoices", label: "Service Invoice Report", icon: ChartBar, id: "nav-service-invoice-report", perms: ["service.view", "reports.view"], group: "Service" },
  { to: "/hr/employees", label: "Employees", icon: UsersThree, id: "nav-hr-employees", perm: "hr.view", group: "HR" },
  { to: "/hr/attendance", label: "Attendance", icon: ClipboardText, id: "nav-hr-attendance", perm: "hr.view", group: "HR" },
  { to: "/hr/leaves", label: "Leaves", icon: ClipboardText, id: "nav-hr-leaves", perm: "hr.view", group: "HR" },
  { to: "/hr/holidays", label: "Holidays", icon: CalendarBlank, id: "nav-hr-holidays", perm: "hr.view", group: "HR" },
  { to: "/hr/reports/attendance", label: "Attendance Report", icon: ChartBar, id: "nav-hr-att-report", perm: "hr.view", group: "HR" },
  { to: "/hr/payroll", label: "Payroll", icon: Wallet, id: "nav-hr-payroll", perm: "hr.payroll.view", group: "HR" },
  { to: "/hr/reports/pf-esi", label: "PF/ESI Report", icon: Bank, id: "nav-hr-pfesi-report", perm: "hr.payroll.view", group: "HR" },
  { to: "/accessories", label: "Accessories", icon: PackageIcon, id: "nav-accessories", perm: "accessories.view", group: "Accessories" },
  { to: "/reports", label: "Reports", icon: ChartBar, id: "nav-reports", end: true, perm: "reports.view", group: "Reporting" },
  { to: "/reports/profit-per-vehicle", label: "Profit / Vehicle", icon: ChartBar, id: "nav-profit-per-vehicle", perm: "reports.profit_per_vehicle", group: "Reporting" },
  { to: "/reports/extended-warranty", label: "Warranty Sales", icon: ShieldCheck, id: "nav-extended-warranty", perm: "reports.view", group: "Reporting" },
  { to: "/vouchers", label: "Vouchers", icon: ArrowsDownUp, id: "nav-vouchers", perm: "vouchers.view", group: "Accounts" },
  { to: "/accounts", label: "Accounts", icon: Wallet, id: "nav-accounts", perm: "accounts.view", group: "Accounts" },
  { to: "/receipts", label: "Receipts", icon: Receipt, id: "nav-receipts", perm: "accounts.view", group: "Accounts" },

  { to: "/tasks", label: "Tasks", icon: ClipboardText, id: "nav-tasks", group: "General" },
  { to: "/complaints", label: "Complaints", icon: WarningCircle, id: "nav-complaints", perm: "complaints.view", group: "General" },
  { to: "/branches", label: "Branches", icon: Buildings, id: "nav-branches", perm: "branches.view", group: "Masters" },
  { to: "/branch-types", label: "Branch Types", icon: StackSimple, id: "nav-branch-types", perm: "branch_types.manage", group: "Masters" },
  { to: "/users", label: "Users", icon: UsersThree, id: "nav-users", perm: "users.view", group: "Admin" },
  { to: "/roles", label: "Roles", icon: ShieldCheck, id: "nav-roles", perm: "roles.manage", group: "Admin" },
  { to: "/audit-log", label: "Audit Log", icon: ClipboardText, id: "nav-audit", perm: "audit.view", group: "Admin" },
  { to: "/admin/bulk-close", label: "Bulk Cleanup", icon: Broom, id: "nav-bulk-cleanup", adminOnly: true, group: "Admin" },
  { to: "/admin/invoice-renumber", label: "Invoice Renumbering", icon: Hash, id: "nav-invoice-renumber", adminOnly: true, group: "Admin" },
  { to: "/settings", label: "Settings", icon: Gear, id: "nav-settings", perm: "settings.view", group: "Admin" },

];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showChangePwd, setShowChangePwd] = useState(false);
  const visibleNav = useMemo(
    () => NAV.filter((item) => {
      if (user?.role === "admin") return true;
      const perms = user?.permissions || [];
      if (item.adminOnly) return false;
      if (item.perm) return perms.includes(item.perm);
      if (item.perms) return item.perms.some((perm) => perms.includes(perm));
      return true; // unrestricted items (dashboard, tasks)
    }),
    [user?.role, user?.permissions]
  );

  if (isQuoteOnlyUser(user)) return <Navigate to="/dse" replace />;
  if (isGateOnlyUser(user)) return <Navigate to="/gate" replace />;

  return (
    <div className="min-h-screen flex bg-canvas">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-canvas-panel border-r border-canvas-line flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-canvas-line">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-ink flex items-center justify-center">
              <Lightning size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-base text-ink leading-none">
                MOTORLEDGER
              </div>
              <div className="font-mono text-[10px] text-ink-subtle mt-1 tracking-widest">
                DEALER OS / v1.0
              </div>
            </div>
          </div>
        </div>

        <NavGroups items={visibleNav} />

        <div className="border-t border-canvas-line p-4">
          <div className="font-mono text-[10px] text-ink-subtle tracking-widest uppercase mb-2">
            Operator
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-ink truncate">
                {user?.name || "—"}
              </div>
              <div className="font-mono text-[11px] text-ink-muted truncate">
                {user?.email}
              </div>
              <div className="font-mono text-[10px] text-ink-subtle uppercase tracking-wider">
                {user?.role}
              </div>
            </div>
            <button
              data-testid="change-password-button"
              onClick={() => setShowChangePwd(true)}
              className="p-2 border border-canvas-line hover:border-ink transition-colors"
              aria-label="Change password"
              title="Change my password"
            >
              <Key size={16} weight="bold" />
            </button>
            <button
              data-testid="logout-button"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="p-2 border border-canvas-line hover:border-ink transition-colors"
              aria-label="Logout"
            >
              <SignOut size={16} weight="bold" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>

      <ChangePasswordDialog open={showChangePwd} onClose={() => setShowChangePwd(false)} />
    </div>
  );
}
